

import { useIntl } from '@umijs/max';
import {
  Alert,
  Empty,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createStyles } from 'antd-style';
import dayjs from 'dayjs';
import React from 'react';
import CopyText from '@/components/CopyText';
import {
  getAdminUserLoginHistory,
  getAdminUserSessions,
} from '@/services/auth/api';
import { extractErrorMessage } from '@/services/auth/errorCodes';

const { Text } = Typography;

const useStyles = createStyles(({ css, token }) => ({
  sectionHeader: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 4px 0 8px;
  `,
  sectionTitle: css`
    font-weight: 600;
    color: ${token.colorText};
  `,
  block: css`
    margin-bottom: 18px;
  `,
}));

const DEFAULT_PAGE_SIZE = 20;

const LOCATION_NOISE = new Set(['Local Network', 'Local', 'Unknown']);

const formatLocation = (loc?: AuthAPI.LocationInfo) => {
  if (!loc) return '-';
  const parts = [loc.country, loc.region, loc.city].filter(
    (s): s is string => !!s && !LOCATION_NOISE.has(s),
  );
  return parts.length > 0 ? parts.join(' · ') : '-';
};

const formatDevice = (d?: AuthAPI.DeviceInfo) => {
  if (!d) return '-';
  const parts: string[] = [];
  if (d.browser)
    parts.push(
      d.browserVersion ? `${d.browser} ${d.browserVersion}` : d.browser,
    );
  if (d.os) parts.push(d.osVersion ? `${d.os} ${d.osVersion}` : d.os);
  return parts.length > 0 ? parts.join(' · ') : d.deviceType || '-';
};

interface Props {
  userId: number;
}

const LoginHistoryPanel: React.FC<Props> = ({ userId }) => {
  const intl = useIntl();
  const { styles } = useStyles();

  const [statusFilter, setStatusFilter] = React.useState<
    'all' | 'success' | 'failed'
  >('all');
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyData, setHistoryData] = React.useState<
    AuthAPI.LoginHistoryRecord[]
  >([]);
  const [historyTotal, setHistoryTotal] = React.useState(0);
  const [historyPage, setHistoryPage] = React.useState(1);
  const [historyError, setHistoryError] = React.useState('');

  const [sessionsLoading, setSessionsLoading] = React.useState(false);
  const [sessionsData, setSessionsData] = React.useState<AuthAPI.SessionInfo[]>(
    [],
  );
  const [sessionsError, setSessionsError] = React.useState('');

  const loadHistory = React.useCallback(
    async (page: number, status: 'all' | 'success' | 'failed') => {
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const resp = await getAdminUserLoginHistory(userId, {
          page,
          pageSize: DEFAULT_PAGE_SIZE,
          ...(status !== 'all' ? { status } : {}),
        });
        if (resp?.success && resp.data) {
          setHistoryData(resp.data.records || []);
          setHistoryTotal(resp.data.total || 0);
          setHistoryPage(resp.data.page || page);
        } else {
          setHistoryData([]);
          setHistoryTotal(0);
          setHistoryError(resp?.message || resp?.error?.code || '');
        }
      } catch (e: unknown) {
        setHistoryError(extractErrorMessage(e, '加载登录历史失败'));
      } finally {
        setHistoryLoading(false);
      }
    },
    [userId],
  );

  const loadSessions = React.useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError('');
    try {
      const resp = await getAdminUserSessions(userId, {
        page: 1,
        pageSize: 50,
        active: true,
      });
      if (resp?.success && resp.data) {
        setSessionsData(resp.data.sessions || []);
      } else {
        setSessionsData([]);
        setSessionsError(resp?.message || resp?.error?.code || '');
      }
    } catch (e: unknown) {
      setSessionsError(extractErrorMessage(e, '加载活跃会话失败'));
    } finally {
      setSessionsLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    void loadHistory(1, statusFilter);
  }, [loadHistory, statusFilter]);

  React.useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const historyColumns: ColumnsType<AuthAPI.LoginHistoryRecord> = [
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.login.time',
        defaultMessage: '时间',
      }),
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.login.status',
        defaultMessage: '结果',
      }),
      dataIndex: 'status',
      width: 100,
      render: (v: string, record) => (
        <Space size={4} direction="vertical" style={{ lineHeight: 1.3 }}>
          <Tag style={{ marginInlineEnd: 0 }}>
            {v === 'success'
              ? intl.formatMessage({
                  id: 'pages.users.detail.login.success',
                  defaultMessage: '成功',
                })
              : intl.formatMessage({
                  id: 'pages.users.detail.login.failed',
                  defaultMessage: '失败',
                })}
          </Tag>
          {record.failReason && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.failReason}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.login.method',
        defaultMessage: '方式',
      }),
      dataIndex: 'loginMethod',
      width: 90,
      render: (v: string) => (
        <Tag style={{ marginInlineEnd: 0 }}>{v || '-'}</Tag>
      ),
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      width: 170,
      render: (v: string) =>
        v ? (
          <CopyText code text={v} style={{ whiteSpace: 'nowrap' }}>
            {v}
          </CopyText>
        ) : (
          '-'
        ),
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.login.location',
        defaultMessage: '位置',
      }),
      width: 160,
      render: (_, record) => formatLocation(record.location),
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.login.device',
        defaultMessage: '设备',
      }),
      render: (_, record) => formatDevice(record.deviceInfo),
      ellipsis: true,
    },
  ];

  const sessionColumns: ColumnsType<AuthAPI.SessionInfo> = [
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.session.loginAt',
        defaultMessage: '登录时间',
      }),
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.session.lastActive',
        defaultMessage: '最近活跃',
      }),
      dataIndex: 'lastActiveAt',
      width: 160,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      width: 170,
      render: (v: string) =>
        v ? (
          <CopyText code text={v} style={{ whiteSpace: 'nowrap' }}>
            {v}
          </CopyText>
        ) : (
          '-'
        ),
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.session.location',
        defaultMessage: '位置',
      }),
      width: 160,
      render: (_, record) => formatLocation(record.location),
    },
    {
      title: intl.formatMessage({
        id: 'pages.users.detail.session.device',
        defaultMessage: '设备',
      }),
      render: (_, record) => formatDevice(record.deviceInfo),
      ellipsis: true,
    },
  ];

  return (
    <div>
      <div className={styles.block}>
        <div className={styles.sectionHeader}>
          <Space>
            <span className={styles.sectionTitle}>
              {intl.formatMessage({
                id: 'pages.users.detail.login.title',
                defaultMessage: '登录历史',
              })}
            </span>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {intl.formatMessage(
                {
                  id: 'pages.users.detail.login.total',
                  defaultMessage: '共 {n} 条',
                },
                { n: historyTotal },
              )}
            </Text>
          </Space>
          <Segmented
            size="small"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as 'all' | 'success' | 'failed')}
            options={[
              {
                label: intl.formatMessage({
                  id: 'pages.users.detail.login.filter.all',
                  defaultMessage: '全部',
                }),
                value: 'all',
              },
              {
                label: intl.formatMessage({
                  id: 'pages.users.detail.login.success',
                  defaultMessage: '成功',
                }),
                value: 'success',
              },
              {
                label: intl.formatMessage({
                  id: 'pages.users.detail.login.failed',
                  defaultMessage: '失败',
                }),
                value: 'failed',
              },
            ]}
          />
        </div>
        {historyError && (
          <Alert
            type="error"
            message={historyError}
            showIcon
            closable
            onClose={() => setHistoryError('')}
            style={{ marginBottom: 8 }}
          />
        )}
        <Table
          rowKey="id"
          size="small"
          loading={historyLoading}
          columns={historyColumns}
          dataSource={historyData}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={intl.formatMessage({
                  id: 'pages.users.detail.login.empty',
                  defaultMessage: '无登录记录',
                })}
              />
            ),
          }}
          pagination={{
            current: historyPage,
            pageSize: DEFAULT_PAGE_SIZE,
            total: historyTotal,
            showSizeChanger: false,
            size: 'small',
            onChange: (page) => loadHistory(page, statusFilter),
          }}
          scroll={{ x: 880 }}
        />
      </div>

      <div className={styles.block}>
        <div className={styles.sectionHeader}>
          <Space>
            <span className={styles.sectionTitle}>
              {intl.formatMessage({
                id: 'pages.users.detail.session.title',
                defaultMessage: '当前活跃会话',
              })}
            </span>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {intl.formatMessage(
                {
                  id: 'pages.users.detail.session.total',
                  defaultMessage: '{n} 个设备在线',
                },
                { n: sessionsData.length },
              )}
            </Text>
          </Space>
        </div>
        {sessionsError && (
          <Alert
            type="error"
            message={sessionsError}
            showIcon
            closable
            onClose={() => setSessionsError('')}
            style={{ marginBottom: 8 }}
          />
        )}
        <Spin spinning={sessionsLoading}>
          <Table
            rowKey="id"
            size="small"
            columns={sessionColumns}
            dataSource={sessionsData}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={intl.formatMessage({
                    id: 'pages.users.detail.session.empty',
                    defaultMessage: '当前无活跃会话',
                  })}
                />
              ),
            }}
            scroll={{ x: 880 }}
          />
        </Spin>
      </div>
    </div>
  );
};

export default LoginHistoryPanel;
